import {
  Competition,
  Leaderboard,
  Project,
  SubmissionsByTask,
  UserSlicedLeaderboard,
  WonPrize,
  XPData,
} from '../../page_components/PoolDetails/Missions/types'

import { PayloadAction, createSlice } from '@reduxjs/toolkit'

export type MissionCondition = {
  type: string
  attachmentId: number
}

export type EthAddress = `0x${string}`

export type SupportedToken = {
  id: string
  name: string
  symbol: string
  logoUrl: string
  chain: string
  decimals: number
  contractAddress: EthAddress
  isFeatured: boolean
  isNativeToken: boolean
}

export type RewardType =
  | 'erc20'
  | 'erc721'
  | 'native'
  | 'discord-role'
  | 'code'
  | 'link'
  | 'custom_success_message'
  | 'solana_nft'

export const rewardTypeRef: Record<RewardType, string> = {
  erc20: 'Token',
  erc721: 'NFT',
  native: 'Token',
  'discord-role': 'Discord Role',
  code: 'Voucher Code',
  solana_nft: 'NFT',
  link: 'Digital Product',
  custom_success_message: '',
}

export type MissionsConditions = {
  type: string
  conditions: MissionCondition[]
}

export enum PaymentType {
  FIAT = 'fiat',
  CRYPTO = 'crypto',
  LOYALTY_POINTS = 'loyalty_points',
}

export enum FiatCurrency {
  USD = 'usd',
}

export enum PaymentChain {
  POLYGON = 'polygon_137',
  SOLANA = 'solana_101',
}
export type DownloadableFile = { name: string; url: string }

export enum ContentBlockType {
  TEXT = 'text',
  IMAGE = 'image',
  BUTTON = 'button',
}

export type ContentBlockMetadata = {
  [key: string]: any
}

export type ContentBlock = {
  id: string
  type: ContentBlockType
  text?: string
  imageUrl?: string
  buttonText?: string
  buttonUrl?: string
  metadata?: ContentBlockMetadata
  order?: number
}

export type ShopItem = {
  id: string
  shopItemAddress?: EthAddress
  token: number
  name: string
  slug: string
  chainId: number | null
  deployedBy?: EthAddress
  quantity: number
  isAvailable: boolean
  price: number
  paymentType: PaymentType
  fiatCurrency: FiatCurrency
  paymentChain?: PaymentChain
  maxPerUser: number
  missionConditions: MissionsConditions
  description: string
  rewardType: RewardType
  requiredLevel: number | null
  rewardAmount: number
  purchases: string[]
  image: string
  publishedOn: string
  project: number
  createdAt: string
  isFeaturedItem: boolean
  updatedAt: string
  token_info: SupportedToken
  quantityOwned: number
  nftTokenIds?: string[]
  nftContractAddress?: string
  quantityRemaining: number
  userFailedClaimAt?: Date
  code?: string
  link?: string
  customSuccessMessage?: string
  ['Project.slug']?: string
  buttonText?: string
  order?: number
  files?: DownloadableFile[]
  content_blocks: ContentBlock[]
}

export type MenuProject = {
  name: string
  slug: string
  logo: string
}

export type UserProjects = {
  user: MenuProject[]
  recommended: MenuProject[]
  trending: MenuProject[]
}

export type UserProjectPoints = {
  id: number
  account: number
  project: number
  amount: number
  isEscrow: boolean
  attachmentId: string
  attachmentType: string
}

export type UserProjectXps = {
  id: number
  accountId: number
  projectId: number
  xp: number
  taskSubmissionId: number
}

export interface MissionControlSlice {
  currentProject: Project | null
  taskSubmissions: SubmissionsByTask | null
  pointsRecords: UserProjectPoints[] | null
  xpRecords: UserProjectXps[] | null
  xpData: XPData | null
  headerXpData: XPData | null
  leaderboard: Leaderboard | null
  userSlicedLeaderboard: UserSlicedLeaderboard | null
  userHasProject: boolean
  isUserBlocked: boolean
  userProjects: UserProjects | null
  taskToAnimate: number | null
  hasSignupBonus: boolean
  totalAllocation: number
  totalAllocationToAllParticipants: number
  expectedRealAllocation: number
  pricePerToken: number
}

export const defaultMissionControl: MissionControlSlice = {
  currentProject: null,
  taskSubmissions: null,
  pointsRecords: null,
  xpRecords: null,
  xpData: null,
  headerXpData: null,
  leaderboard: null,
  userSlicedLeaderboard: null,
  userHasProject: false,
  isUserBlocked: false,
  userProjects: null,
  taskToAnimate: null,
  hasSignupBonus: false,
  totalAllocation: 0,
  totalAllocationToAllParticipants: 0,
  expectedRealAllocation: 0,
  pricePerToken: 0,
}

const initialState: MissionControlSlice = defaultMissionControl

export const missionControl = createSlice({
  name: 'missionControl',
  initialState,
  reducers: {
    setCurrentProject: (state, action) => {
      state.currentProject = action.payload
      return state
    },
    setTaskSubmissions: (state, action) => {
      state.taskSubmissions = action.payload
      return state
    },
    setPointsRecords: (state, action) => {
      state.pointsRecords = action.payload
      return state
    },
    setXpRecords: (state, action) => {
      state.xpRecords = action.payload
      return state
    },
    setXPData: (state, action) => {
      state.xpData = action.payload
      return state
    },
    setHeaderXPData: (state, action) => {
      state.headerXpData = action.payload
      return state
    },
    setLeaderboard: (state, action: PayloadAction<Leaderboard>) => {
      state.leaderboard = action.payload
      return state
    },
    setUserSlicedLeaderboard: (
      state,
      action: PayloadAction<UserSlicedLeaderboard>,
    ) => {
      state.userSlicedLeaderboard = action.payload
      return state
    },
    setUserHasProject: (state, action) => {
      state.userHasProject = action.payload
      return state
    },
    setIsUserBlocked: (state, action) => {
      state.isUserBlocked = action.payload
      return state
    },
    setUserProjects: (state, action) => {
      state.userProjects = action.payload
      return state
    },
    setTaskToAnimate: (state, action) => {
      state.taskToAnimate = action.payload
      return state
    },
    setHasSignupBonus: (state, action) => {
      state.hasSignupBonus = action.payload
      return state
    },
    setTotalAllocation: (state, action) => {
      state.totalAllocation = action.payload
    },
    setTotalAllocationToAllParticipants: (state, action) => {
      state.totalAllocationToAllParticipants = action.payload
    },
    setExpectedRealAllocation: (state, action) => {
      state.expectedRealAllocation = action.payload
    },
    setPricePerToken: (state, action) => {
      state.pricePerToken = action.payload
    },
  },
})

export const {
  setCurrentProject,
  setTaskSubmissions,
  setPointsRecords,
  setXpRecords,
  setXPData,
  setHeaderXPData,
  setLeaderboard,
  setUserSlicedLeaderboard,
  setUserHasProject,
  setIsUserBlocked,
  setUserProjects,
  setTaskToAnimate,
  setHasSignupBonus,
  setTotalAllocation,
  setTotalAllocationToAllParticipants,
  setExpectedRealAllocation,
  setPricePerToken,
} = missionControl.actions

export default missionControl.reducer
